import { Button, Stack } from "@mui/material";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import Divider from "components/Divider/Divider";
import { Controller } from "react-hook-form";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps, filteredFalsyData } from "../utils";
import Datatable from "components/Datatable/Datatable";
import { useTranslation } from "react-i18next";
import { checkNonMoneyDataForTable } from "utils/utils";
import TerminalDialogContent from "../Component/TerminalDialogContent";
import {
  queryTerminalList,
  updateTerminal,
  queryInstallationDate,
  queryMaintenanceParties,
  queryTerminalModelList,
  queryEmployeeList,
  checkTerminalSNDuplicated,
} from "../model";
import { spinning } from "../utils";
import Selection from "components/Input/Selection/Selection";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
const MaintenanceRecordCache = {};

const MerchantMaintenanceRecord = ({
  merchantId,
  serviceIntegratorOptions,
}) => {
  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
  });
  // Local keys
  const BUTTON_LOCAL_KEY = "page.merchants.merchantDetail.button";
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.maintenanceRecord";
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  // remove dialog state
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [onFocusTerminal, setOnFocusTerminal] = useState({});

  // Terminal dialog state
  const [openTerminalDialog, setOpenTerminalDialog] = useState(false);
  const terminalDialogAction = useRef("");
  const terminalEditTarget = useRef({});
  const terminalFormRef = useRef(null);

  const { t } = useTranslation();
  const [datasource, setDatasource] = useState({
    data: [],
    total: 0,
    page: 0,
    size: 20,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });
  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const handleSearch = async (data) => {
    console.log("handleSearch", data);
    console.log("after fileredData", filteredFalsyData(data));
    setLoading(true);
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      sort: "createdDate,desc",
      merchantId,
      ...filteredFalsyData(data),
    };
    const apiResponse = await queryTerminalList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout();
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 20,
              data: [],
            });
          }
        }
      }
    }
    setLoading(false);
  };

  const fetchTerminalList = async (action) => {
    try {
      setLoading(true);
      // Check cache first
      if (!action && MaintenanceRecordCache[merchantId].terminalList) {
        setDatasource(MaintenanceRecordCache[merchantId].terminalList);
        return;
      }
      // Only fetch if not in cache
      const terminalListResp = await queryTerminalList({
        merchantId,
        size: 20,
        page: 0,
        sort: "createdDate,desc",
      });
      const { data } = terminalListResp;

      if (data?.responseCode === "0000" && data?.data) {
        MaintenanceRecordCache[merchantId] = {
          ...MaintenanceRecordCache[merchantId],
          terminalList: data,
        };
        setDatasource(data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
      delete MaintenanceRecordCache[merchantId];
    } finally {
      setLoading(false);
    }
  };

  const initialFetch = async () => {
    try {
      setLoading(true);
      if (MaintenanceRecordCache[merchantId]) {
        setDatasource(MaintenanceRecordCache[merchantId].terminalList);
        return;
      }

      const [
        terminalListResp,
        installationDateResp,
        maintenancePartiesResp,
        terminalModelListResp,
        fullEmployeeListResp,
      ] = await Promise.all([
        queryTerminalList({
          merchantId,
          size: 20,
          page: 0,
          sort: "createdDate,desc",
        }),
        queryInstallationDate({ merchantId }),
        queryMaintenanceParties(),
        queryTerminalModelList(),
        queryEmployeeList({
          page: 0,
          size: 999,
          action: "merchant-detail",
          sort: "createdDate,desc",
          merchantId,
        }),
      ]);

      const cacheData = {};
      const handlers = [
        {
          response: terminalListResp,
          key: "terminalList",
          onProcess: (data) => setDatasource(data),
        },
        { response: installationDateResp, key: "installationDate" },
        { response: maintenancePartiesResp, key: "maintenanceParties" },
        { response: terminalModelListResp, key: "terminalModel" },
        { response: fullEmployeeListResp, key: "fullEmployeeList" },
      ];

      // Process each response and populate cacheData
      handlers.forEach(({ response, key, onProcess }) => {
        if (response?.data?.responseCode === "0000" && response?.data?.data) {
          cacheData[key] = response.data;
          onProcess?.(response.data);
        }
      });
      cacheData["serviceIntegratorOptions"] = serviceIntegratorOptions;

      // Update cache only if there's new data
      if (Object.keys(cacheData).length > 0) {
        MaintenanceRecordCache[merchantId] = {
          ...MaintenanceRecordCache[merchantId],
          ...cacheData,
        };
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
      delete MaintenanceRecordCache[merchantId];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (merchantId) {
      initialFetch();
    }
  }, [merchantId]);

  const searchFields = () => (
    <>
      <form onSubmit={handleSearch}>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="employeeId"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  errorMessage={error?.message || ""}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                  {...CustomComponentCommonProps(
                    t(`${PAGE_LOCALE_KEY}.searchFields.employeeId`),
                    value,
                    "OutlinedText",
                    false,
                    true,
                  )}
                />
              )}
            />
            <Controller
              name="terminalSerialNumber"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  errorMessage={error?.message || ""}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                  {...CustomComponentCommonProps(
                    t(`${PAGE_LOCALE_KEY}.searchFields.terminalSerialNumber`),
                    value,
                    "OutlinedText",
                    false,
                    true,
                  )}
                />
              )}
            />
            <Controller
              name="terminalType"
              defaultValue={""}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Selection
                  errorMessage={error?.message || ""}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                  options={[
                    {
                      key: "TERMINAL",
                      title: "TERMINAL",
                      value: "TERMINAL",
                    },
                    {
                      key: "ACCESSORY",
                      title: "ACCESSORY",
                      value: "ACCESSORY",
                    },
                  ]}
                  {...CustomComponentCommonProps(
                    t(`${PAGE_LOCALE_KEY}.searchFields.terminalType`),
                    value,
                    "Selection",
                    false,
                    true,
                  )}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          justifyContent={"flex-end"}
        >
          <LoadingButton
            variant="outline-normal-button"
            onClick={async () => {
              reset({
                employeeId: "",
                terminalType: "",
                terminalSerialNumber: "",
              });
              await fetchTerminalList("fetch");
            }}
            disabled={loading}
          >
            {t(`${BUTTON_LOCAL_KEY}.reset`)}
          </LoadingButton>
          <LoadingButton
            variant="normal-button"
            onClick={handleSubmit(handleSearch)}
            type="submit"
            loading={loading}
          >
            {t(`${BUTTON_LOCAL_KEY}.search`)}
          </LoadingButton>
        </Stack>
      </form>
    </>
  );

  const columns = [
    {
      key: "employeeId",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.employeeId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "terminalSerialNumber",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.terminalSerialNumber"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "terminalType",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.terminalType"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "terminalModel",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.terminalModel"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "maintenancePartyName",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.maintenancePartyName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "serviceIntegratorName",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.serviceIntegratorName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "installationDate",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.installationDate"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "remarks",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.remarks"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "isSpareTerminal",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.isSpareTerminal"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + ".columnTitle.action"),
      sorter: false,
      render: (data) => {
        return (
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Button
              sx={{ color: "#309ea7" }}
              onClick={() => {
                terminalDialogAction.current = "UPDATE";
                terminalEditTarget.current = data;
                setOpenTerminalDialog(true);
              }}
            >
              {t(`${BUTTON_LOCAL_KEY}.edit`)}
            </Button>
            <Button
              sx={{ color: "#309ea7" }}
              onClick={() => {
                setOnFocusTerminal(data);
                setOpenRemoveDialog(true);
              }}
            >
              {t(`${BUTTON_LOCAL_KEY}.remove`)}
            </Button>
          </Stack>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const handleDeleteTerminal = async () => {
    console.log("handleDeleteTerminal", onFocusTerminal);
    try {
      setLoading(true);
      const deleteTerminalResp = await updateTerminal({
        ...onFocusTerminal,
        status: "NOT_OCCUPIED",
        employeeId: null,
      });
      const { data } = deleteTerminalResp;
      if (data?.responseCode === "0000") {
        snackbar("Terminal removed success", "success");
      } else {
        snackbar("Terminal removed fail", "error");
      }
    } catch (error) {
      console.error("Error deleting terminal:", error);
    } finally {
      await fetchTerminalList("fetch");
      setOpenRemoveDialog(false);
      setLoading(false);
    }
  };

  const handleTerminalFormSubmit = async (formData, action) => {
    console.log("Action:", action);
    let terminalPayload;

    try {
      setLoading(true);
      if (action === "ADD") {
        const checkSNResponse = await checkTerminalSNDuplicated({
          terminalSerialNumber: formData.terminalSerialNumber,
        });

        if (checkSNResponse?.data?.responseCode !== "0000") {
          snackbar("Serial Number duplication check failed", "error");
          return;
        }
      }
      terminalPayload = {
        ...formData,
        status: formData.isSpareTerminal ? "SPARE" : "ACTIVE",
        employeeId:
          formData.terminalType === "TERMINAL" && !formData.isSpareTerminal
            ? formData.employeeId
            : null,
        merchantId,
        ...(action === "UPDATE" && {
          terminalId: terminalEditTarget.current.terminalId,
        }),
      };
      const response = await updateTerminal(terminalPayload);
      const isSuccess = response?.data?.responseCode === "0000";
      snackbar(
        isSuccess
          ? `${action === "ADD" ? "Add" : "Update"} terminal/accessory success`
          : `${action === "ADD" ? "Add" : "Update"} terminal/accessory failed`,
        isSuccess ? "success" : "error",
      );
      await fetchTerminalList("fetch");
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      terminalFormRef.current = null;
      setOpenTerminalDialog(false);
      setLoading(false);
    }
  };
  const terminalDialogContent = (action) => {
    return (
      <TerminalDialogContent
        action={action}
        onSubmit={handleTerminalFormSubmit} // Handle submission logic
        setFormSubmitHandler={(submitHandler) => {
          terminalFormRef.current = submitHandler;
        }}
        options={MaintenanceRecordCache[merchantId]}
        terminalEditTarget={terminalEditTarget.current}
      />
    );
  };

  console.log("Maintenance Record Tab cache: ", MaintenanceRecordCache);
  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      {searchFields()}
      <Divider style={{ margin: "24px 0" }} />
      <Stack direction={"row"} justifyContent={"flex-end"} sx={{ mb: 2 }}>
        <Button
          variant="normal-button"
          onClick={() => {
            terminalDialogAction.current = "ADD";
            setOpenTerminalDialog(true);
          }}
          disabled={loading}
        >
          {t(`${BUTTON_LOCAL_KEY}.add`)}
        </Button>
      </Stack>
      <Stack>
        {loading ? (
          spinning()
        ) : (
          <Datatable
            columns={columns}
            // columnsPreference={columnsArray}
            datasource={datasource?.data}
            total={datasource?.total}
            pagination={pagination}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSizeInfoRef={pageSizeInfoRef}
            handleSearch={handleSearch}
            // search={search}
          />
        )}
      </Stack>

      {/* Remove Terminal */}
      <SimpleDialog
        open={openRemoveDialog}
        dialogOncloseFn={() => {
          setOpenRemoveDialog(false);
        }}
        title={t(PAGE_LOCALE_KEY + ".removeDialog.title")}
        content={t(PAGE_LOCALE_KEY + ".removeDialog.content")}
        confirmFn={handleDeleteTerminal}
        positiveAction={t("component.simple.dialog.action.1")}
        positiveActionLoading={loading}
        negativeActionDisabled={loading}
        negativeAction={t("component.simple.dialog.action.2")}
      />

      {/* Add / Update Terminal */}
      <SimpleDialog
        open={openTerminalDialog}
        dialogOncloseFn={() => {
          setOpenTerminalDialog(false);
          terminalFormRef.current = null;
        }}
        title={
          terminalDialogAction.current === "ADD"
            ? t(PAGE_LOCALE_KEY + ".terminalDialog.addTitle")
            : t(PAGE_LOCALE_KEY + ".terminalDialog.updateTitle")
        }
        content={terminalDialogContent(terminalDialogAction.current)}
        confirmFn={async () => {
          if (terminalFormRef.current) {
            terminalFormRef.current();
          }
        }}
        positiveAction={t(PAGE_LOCALE_KEY + ".terminalDialog.confirm")}
        positiveActionLoading={loading}
      />
    </Stack>
  );
};

export default React.memo(MerchantMaintenanceRecord);
